import { OrgsApi } from 'api-client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import PageHeader from '@/lib/base/page-layout/PageHeader';
import { getApiBaseUrl } from '@/lib/config';
import { logger } from '@/lib/logger';
import { sanitizeError } from '@/lib/utils/errors';

import { useUser } from '../../allauth_auth/hooks.js';
import { getApiConfiguration } from '../../api/utils';
import { useOrg } from '../../contexts/OrgContext';
import { useTitle } from '../../hooks/useTitle';

// =============================================================================
// CreateOrgFormContent — inner component that accesses FormContext
// =============================================================================

interface CreateOrgFormContentProps {
  formData: { name: string };
  loading: boolean;
  apiError: string | null;
  onNameChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onDismissError: () => void;
}

function CreateOrgFormContent({
  formData,
  loading,
  apiError,
  onNameChange,
  onSubmit,
  onDismissError,
}: CreateOrgFormContentProps) {
  const { touchAll, isValid, errorSummary, scrollToField } = useFormContext();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValid) {
        touchAll();
        if (errorSummary.length > 0) {
          scrollToField(errorSummary[0].name);
        }
        return;
      }
      await onSubmit(e);
    },
    [touchAll, isValid, errorSummary, scrollToField, onSubmit]
  );

  return (
    <form onSubmit={handleSubmit} noValidate className='space-y-6'>
      {apiError && (
        <div
          role='alert'
          className='bg-error-50 border border-error-200 rounded-xl p-6 text-center mb-6'
        >
          <div className='w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4'>
            <svg
              className='w-6 h-6 text-error-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
          </div>
          <h3 className='text-lg font-semibold text-fg-primary mb-2'>
            Creation failed
          </h3>
          <p className='text-fg-quaternary mb-4'>{apiError}</p>
          <Button
            color='primary-destructive'
            size='sm'
            onClick={onDismissError}
          >
            Try again
          </Button>
        </div>
      )}

      <div className='space-y-4'>
        <FormField
          name='name'
          label='Organization name'
          isRequired
          hint='Choose a unique name that represents your organization'
        >
          {({ isInvalid, showError, inputId }) => (
            <InputGroup
              isInvalid={isInvalid}
              showError={showError}
              value={formData.name}
              onChange={onNameChange}
              isDisabled={loading}
              aria-label='Organization name'
            >
              <InputBase
                id={inputId}
                placeholder='Enter your organization name'
                autoFocus
                maxLength={100}
              />
            </InputGroup>
          )}
        </FormField>
      </div>

      {/* Actions */}
      <div className='flex gap-3 pt-6 border-t border-border-secondary'>
        <Button
          type='submit'
          color='primary'
          size='lg'
          className='flex-1'
          isDisabled={loading}
        >
          {loading ? 'Creating organization…' : 'Create organization'}
        </Button>
        <Link to='/organizations'>
          <Button color='secondary' size='lg'>
            Cancel
          </Button>
        </Link>
      </div>
    </form>
  );
}

// =============================================================================
// CreateTeam — outer component that owns state and wraps FormProvider
// =============================================================================

const CreateTeam: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const canCreateOrg = user?.is_staff === true;

  useTitle('Create organization');
  const { refreshCurrentOrg, setCurrentTeam } = useOrg();
  const [formData, setFormData] = useState({
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Redirect superusers to Django admin panel
  useEffect(() => {
    if (user?.is_superuser) {
      logger.info(
        'Superuser detected on team creation page, redirecting to admin'
      );
      const apiBaseUrl = getApiBaseUrl();
      window.location.href = `${apiBaseUrl}/`;
    }
  }, [user]);

  // Redirect users without org creation permission
  useEffect(() => {
    if (user && !canCreateOrg) {
      navigate('/organizations', { replace: true });
    }
  }, [user, canCreateOrg, navigate]);

  const fieldConfig = useMemo(
    () => ({ name: { required: true } as FieldConfig }),
    []
  );

  const formValues = useMemo(() => ({ name: formData.name }), [formData.name]);

  const handleNameChange = useCallback(
    (value: string) => {
      setFormData(prev => ({ ...prev, name: value }));
      if (apiError) {
        setApiError(null);
      }
    },
    [apiError]
  );

  const handleDismissError = useCallback(() => {
    setApiError(null);
  }, []);

  const handleSubmit = useCallback(
    async (_e: React.FormEvent) => {
      try {
        setLoading(true);
        setApiError(null);
        const api = new OrgsApi(getApiConfiguration());

        const team = await api.teamsCreate({
          team: {
            name: formData.name.trim(),
            slug: undefined,
          } as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- API client team type expects slug but backend allows undefined
        });

        // Directly set the newly created team as current (avoid race condition)
        setCurrentTeam(team);

        // Refresh teams list in background to sync with server
        refreshCurrentOrg();

        // Navigate to the new team's dashboard
        navigate(`/${team.id}`);
      } catch (err: unknown) {
        logger.error(
          'Error creating organization',
          err instanceof Error ? err : undefined,
          { component: 'CreateOrg' }
        );
        let errorDetail: string | undefined;
        if (
          err != null &&
          typeof err === 'object' &&
          'response' in err &&
          err.response instanceof Response
        ) {
          try {
            const body = await (err.response as Response).json();
            errorDetail =
              typeof body?.detail === 'string' ? body.detail : undefined;
          } catch {
            // ignore JSON parse errors
          }
        }
        setApiError(
          errorDetail
            ? sanitizeError(errorDetail)
            : "We couldn't create the organization. Try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [formData.name, refreshCurrentOrg, navigate, setCurrentTeam]
  );

  if (!user) {
    return null;
  }

  return (
    <div className='min-h-full bg-neutral-50'>
      <div className='max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6'>
        {/* Breadcrumb */}
        <nav className='text-sm mb-4'>
          <ol className='flex items-center gap-2 text-fg-quaternary'>
            <li>
              <Link
                to='/organizations'
                className='hover:text-fg-primary transition-colors'
              >
                Organizations
              </Link>
            </li>
            <li>
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </li>
            <li className='text-fg-primary font-medium'>Create organization</li>
          </ol>
        </nav>

        {/* Page Header */}
        <PageHeader
          title='Create new organization'
          subtitle='Set up your organization to start collaborating with your team'
        />

        {/* Create organization form */}
        <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
          <div className='px-6 py-5 border-b border-border-secondary'>
            <h2 className='text-lg font-semibold text-fg-primary'>
              Organization details
            </h2>
            <p className='text-sm text-fg-quaternary mt-1'>
              Enter basic information about your organization
            </p>
          </div>

          <div className='p-6'>
            <FormProvider fields={fieldConfig} values={formValues}>
              <CreateOrgFormContent
                formData={formData}
                loading={loading}
                apiError={apiError}
                onNameChange={handleNameChange}
                onSubmit={handleSubmit}
                onDismissError={handleDismissError}
              />
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTeam;
